/**
 * **整单优惠的按比例分摊 + 实付派生** —— 唯一实现（C 域 PRD 🆕9 · `DR-040` §4.3 · `DR-084`）
 *
 * ## 为什么这个文件必须独立存在
 *
 * C 域 PRD 的表里写着：「整单优惠的分摊函数**唯一实现在共享层**
 * （`packages/shared/src/nucleus/discount.ts`）—— Web / 小程序 / 服务端**共用**；
 * 🔴 **端侧不得重写分摊或取整**（只转发文案）」。
 *
 * 若端侧自己算一遍展示、服务端/另一处再算一遍落库，就会出现
 * **卡面看是摊了 3.20、库里存的是 3.19** —— 而两边都"看起来对"（都是两位小数），
 * 这种偏差只有对账时才会被发现，正是铁律 24 要拦的东西。
 *
 * ## 🔴 口径（`DR-084` 起）：`original` 是**原价**，`amount` 是**派生出来的实付**
 *
 * ```
 * 输入     original_i   = 用户填的「原价」（账单上这一项的价格）
 *          self_i       = 该行自填的优惠（>= 0）
 *          total        = 父级「整单优惠」（不在本文件落库）
 * 分摊     share_i      = allocate(total, original)     // 最大余数法，Σ 逐分恒等
 * 落库     discount_i   = self_i + share_i
 *          amount_i     = original_i − discount_i        // 实付，必须 > 0
 * 派生展示 original_i   = amount_i + discount_i          // 与落库互为反函数
 * ```
 *
 * 🔴 **为什么要这一步减法**：`amount` 恒为**实付净额**（`DR-040` §4.2.2）—— 余额 / 唯一
 * 「支出」口径 / 预算已用 / 报表 / 信用卡账单**全都只看它**。若用户填原价却直接落成
 * `amount`，那"优惠"就只是一行装饰：**账面支出永远比实际多出优惠那一截**
 * （2026-11-04 实测：原价 46.95、优惠 0.53，被记成支出 46.95 而不是 46.42）。
 *
 * ## 🔴 为什么权重只取原价、不再加自填优惠
 *
 * 批 `94` 时期权重写的是 `amount + selfDiscount`（当时 `amount` 是**实付**，两者相加才是原价）。
 * `DR-084` 把录入格改成原价之后，原价**已经包含**那部分减免 ⇒ 再把自填加进去就是
 * **重复计入**（自填 1 元的行会被当成 1 元更贵的商品来分摊）。⇒ 权重 = `original`。
 *
 * ## 🔴 为什么用 `allocate()` 而不是自己写除法
 *
 * `allocate()` 已是全库的**最大余数法**定点实现（`nucleus/money.ts`）：
 * Σ 逐分恒等、平局按索引升序、可复算幂等。本项目**禁浮点除法**（规格 §22.1），
 * 任何 `total × w / Σw` 的手写形态都会在某一分上漂移。
 */

import type { Cents } from '../types/brand.js';
import { allocate, sumCents, toCents } from './money.js';

/** 参与分摊的一行：**原价** + 该行**自己**填的优惠 */
export interface OriginLine {
  /** 原价（整数分，`> 0`）—— 录入界面「原价」格里的那个数 */
  original: Cents;
  /** 该行自填的优惠（整数分，`>= 0`；没有则 0） */
  selfDiscount: Cents;
}

/**
 * 落库腿 → 原价行（**编辑回填的唯一实现**）。
 *
 * `original = amount + discount` 是 `DR-040` §4.2.2 的派生式；把它放在这里，
 * 是为了让"回填原价"和"派生实付"成为**同一个文件里的同一对反函数**
 * —— 二者若各写一份，迟早一处用加法、一处用减法，而差一分也不会报错。
 *
 * ⚠️ 入参取**裸 `number`**：`TransactionLegView` 下发的就是裸数（视图层不进 brand）。
 */
export function storedLegOriginal(leg: { amount: number; discount: number }): Cents {
  return toCents(leg.amount + leg.discount);
}

/** 各行原价之和 —— 整单优惠的**上界**（超过它就意味着"优惠比原价还多"，无经济意义） */
export function sumOriginals(lines: readonly OriginLine[]): Cents {
  return sumCents(lines.map((l) => l.original));
}

/**
 * 把**整单优惠**按各行原价比例分摊（不含各行自填部分）。
 *
 * 🔴 不变量：`sumCents(返回值) === total`（逐分恒等）。`total = 0` ⇒ 全 0。
 *
 * @param lines 参与分摊的行（**顺序即落库顺序** —— 平局按索引升序，改顺序会改结果，故调用方必须传稳定顺序）
 * @param total 整单优惠（整数分，`>= 0`）
 * @throws RangeError 当各行原价之和为 0（正常路径不会：`original > 0` 已被上游挡住）
 */
export function allocateWholeDiscount(lines: readonly OriginLine[], total: Cents): Cents[] {
  if (lines.length === 0) return [];
  if ((total as number) === 0) return lines.map(() => toCents(0));

  const weights = lines.map((l) => l.original as number);
  const sumW = weights.reduce((a, b) => a + b, 0);
  if (sumW <= 0) {
    throw new RangeError('整单优惠无法分摊：各行原价之和为 0');
  }
  return allocate(total, weights);
}

/**
 * **最终每行优惠** = 自填 + 分摊 —— 落库 `legs.discount` 用的就是这个数组。
 *
 * 这是"两个写入者"（父级整单 + 子笔自填）唯一的合流点：
 * `DR-054` D3 拍板保留子笔自填 ⇒ **必须**在这里一次性合成，
 * 否则 UI 显示与落库各算一次 ⇒ 又是同一事实两份载体。
 */
export function finalDiscounts(lines: readonly OriginLine[], total: Cents): Cents[] {
  const shares = allocateWholeDiscount(lines, total);
  return lines.map((l, i) => toCents((l.selfDiscount as number) + (shares[i] as number)));
}

/**
 * **最终每行实付** = 原价 − 最终优惠 —— 落库 `legs.amount` 用的就是这个数组。
 *
 * 🔴 不在这里判 `> 0`：那是**用户输入校验**（要指名"第 N 笔"），由端侧统一报错；
 * 本函数只做派生，返回值可能是 0 或负数（调用方必须自己挡住，契约层 `.positive()` 是最后一道）。
 */
export function paidAmounts(lines: readonly OriginLine[], total: Cents): Cents[] {
  const finals = finalDiscounts(lines, total);
  return lines.map((l, i) => toCents((l.original as number) - (finals[i] as number)));
}
